import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';
import { usePopup } from '../../../contexts/PopupContext';
import { useFriendsRealtimeData } from '../../../hooks/useFriendsRealtimeData';
import { UserListAvatar } from '../../common/UserListAvatar';
import { ProfileStatUsersIcon } from '../ProfileActivityIcons';
import './FriendsWidget.css';

const MAX_AVATARS = 10;

function FriendsWidget() {
  const { t } = useTranslation(['profile', 'shared', 'events']);
  const { openPopup } = usePopup();
  const [currentUser, setCurrentUser] = useState(null);

  const {
    friendsCount,
    friends,
    pendingCount,
    pendingSentCount,
    isLoading,
  } = useFriendsRealtimeData(currentUser?.id);

  useEffect(() => {
    loadCurrentUser();
  }, []);

  const loadCurrentUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUser(user);
    } catch (error) {
      console.error('Error loading current user:', error);
    }
  };

  const handleOpenFriendsManagement = useCallback(() => {
    openPopup('friends-management', {}, 0);
  }, [openPopup]);

  const friendDisplayName = useCallback(
    (friend) =>
      friend.nickname
      || [friend.first_name, friend.last_name].filter(Boolean).join(' ')
      || friend.name
      || t('profile.friends.friendFallbackName', 'Znajomy'),
    [t],
  );

  const handleOpenFriendProfile = useCallback(
    (friend) => {
      if (!friend?.id) return;
      openPopup('profile', { userId: friend.id }, 0);
    },
    [openPopup],
  );

  const count = friendsCount || 0;
  const received = pendingCount || 0;
  const sent = pendingSentCount || 0;
  const visibleFriends = friends.slice(0, MAX_AVATARS);
  const showOverflowDots = count > MAX_AVATARS;

  const getInitials = useCallback((friend) => {
    if (friend.first_name && friend.last_name) {
      return `${friend.first_name[0]}${friend.last_name[0]}`.toUpperCase();
    }
    return friend.name?.slice(0, 2).toUpperCase() || '??';
  }, []);

  if (isLoading) {
    return (
      <div className="fw-widget">
        <div className="fw-loading-placeholder" />
      </div>
    );
  }

  return (
    <div className="fw-widget fw-widget--split">
      <div className="apw-accent" aria-hidden />
      <div className="fw-header">
        <span className="fw-header-icon" aria-hidden>
          <ProfileStatUsersIcon size={20} className="fw-header-icon-svg" />
        </span>
        <span className="fw-header-title">{t('profile.friends.title', 'Znajomi')}</span>
      </div>

      <div className="fw-body fw-body--split">
        <div className="fw-split-left">
          {count > 0 ? (
            <div className="fw-avatar-grid" aria-label={t('profile.friends.title', 'Znajomi')}>
              {visibleFriends.map((friend) => {
                const name = friendDisplayName(friend);
                return (
                  <button
                    key={friend.id}
                    type="button"
                    className="fw-avatar-tile fw-avatar-tile--profile"
                    onClick={() => handleOpenFriendProfile(friend)}
                    aria-label={t('profile.friends.openFriendProfile', 'Profil: {{name}}', { name })}
                  >
                    <UserListAvatar
                      user={friend}
                      className="fw-avatar-tile-img"
                      inactiveTitle={t('events.details.avatarInactiveAria')}
                      bannedTitle={t('events.details.avatarBannedAria')}
                      alt=""
                      fallback={<span className="fw-avatar-tile-initials">{getInitials(friend)}</span>}
                    />
                  </button>
                );
              })}
              {showOverflowDots && (
                <div
                  className="fw-avatar-overflow"
                  title={t('profile.friends.moreFriendsHint', 'Jeszcze {{extra}} znajomych (pokazano 10 awatarów)', {
                    extra: count - MAX_AVATARS,
                  })}
                  aria-label={t('profile.friends.moreFriendsHint', 'Jeszcze {{extra}} znajomych (pokazano 10 awatarów)', {
                    extra: count - MAX_AVATARS,
                  })}
                >
                  <span className="fw-avatar-overflow-dots" aria-hidden>...</span>
                </div>
              )}
            </div>
          ) : (
            <p className="fw-empty-inline">
              {t('profile.friends.noFriendsYet', 'Nie masz jeszcze znajomych')}
            </p>
          )}
        </div>

        <div className="fw-split-right">
          <table className="fw-stats-table">
            <tbody>
              <tr>
                <th scope="row">{t('profile.friends.stats.friends', 'Znajomi')}</th>
                <td>{count}</td>
              </tr>
              <tr>
                <th scope="row">{t('profile.friends.stats.received', 'Otrzymane prośby')}</th>
                <td>{received}</td>
              </tr>
              <tr>
                <th scope="row">{t('profile.friends.stats.sent', 'Wysłane prośby')}</th>
                <td>{sent}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <button type="button" className="fw-cta" onClick={handleOpenFriendsManagement}>
        {t('profile.friends.manageFriends', 'Zarządzaj znajomymi')}
        <span className="fw-cta-arrow">→</span>
      </button>
    </div>
  );
}

export default FriendsWidget;
